import type { FC, MouseEvent, ReactNode } from 'react';
import type { ColorToken } from '@/ui';
import type { MotionComponentProps, PickProps } from '@/ui/types/types';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { AnimatePresence } from 'motion/react';
import { Flex } from '@/ui';
import { ButtonIconWrapper } from '@/ui/Button/ButtonIconWrapper';
import { tokenToVariable } from '@/ui/utils/tokenToVariable';
import styles from './Button.module.css';
import LoaderIcon from './svg/loader.svg?react';

type BaseProps = {
    size?: 's' | 'm' | 'l';
    /** if specified, button will be rendered as <a /> tag */
    href?: string;
} & PickProps<'button', 'type' | 'disabled'>;

type WithChildren = {
    children: ReactNode;
    icon?: ReactNode;
    variant?: 'primary' | 'secondary';
    block?: boolean;
    loading?: boolean;
};

type IconOnly = {
    children?: undefined | null | false;
    icon: ReactNode;
    variant?: never;
    block?: never;
    loading?: never;
};

type AnchorButtonProps = {
    href: string;
} & PickProps<'a', 'onClick' | 'ref'>;

type ButtonProps = {
    href?: never;
} & PickProps<'button', 'onClick' | 'ref'>;

type Props = BaseProps & (WithChildren | IconOnly) & (AnchorButtonProps | ButtonProps);

const isAnchorButton = (
    props: Props
): props is Props & AnchorButtonProps => {
    return Boolean(props.href);
};

const ICON_SIZE: Record<'s' | 'm' | 'l', number> = {
    s: 20,
    m: 26,
    l: 32
};

const DEFAULT_PROPS = {
    variant: 'primary',
    size: 's'
} as const;

const StandartButton: FC<Props> = (props) => {
    const loadingIconWidth = props.size === 's' ? 16 : props.size === 'm' ? 24 : 32;

    const buttonContent = (
        <>
            <Flex align="center">
                <AnimatePresence>
                    {props.loading && (
                        <motion.span
                            initial={{ width: 0, marginRight: 0 }}
                            animate={{ width: loadingIconWidth, marginRight: 8 }}
                            exit={{ width: 0, marginRight: 0 }}
                            transition={{
                                type: 'spring',
                                stiffness: 700,
                                damping: 40
                            }}
                            className={styles.loader}
                        >
                            <LoaderIcon />
                        </motion.span>
                    )}
                </AnimatePresence>
                {props.children}
            </Flex>
            {Boolean(props.icon) && (
                <div className={clsx(styles.iconWrapper)}>
                    <ButtonIconWrapper size={ICON_SIZE[props.size ?? 's']}>
                        {props.icon}
                    </ButtonIconWrapper>
                </div>
            )}
        </>
    );

    const buttonProps = {
        'initial': { scale: 1 },
        'whileHover': { scale: 1.05 },
        'whileTap': {
            scale: 0.9,
            boxShadow: `0 0 ${loadingIconWidth}px ${tokenToVariable<ColorToken>('backgroundPrimary')}`
        },
        'transition': { type: 'spring', stiffness: 800, damping: 40 },
        'className': clsx(
            styles.button,
            styles[`variant-${props.variant}`],
            styles[`size-${props.size}`],
            props.block && styles.block,
            props.icon && styles.icon,
            props.loading && styles.loading
        ),
        'type': props.type ?? 'button',
        'disabled': props.disabled || props.loading,
        'aria-disabled': props.disabled || props.loading,
        'onClick': (e) => {
            if (isAnchorButton(props)) {
                if (props.disabled) {
                    e.preventDefault();
                }
                props.onClick?.(e as MouseEvent<HTMLAnchorElement>);
            }
            else {
                props.onClick?.(e as MouseEvent<HTMLButtonElement>);
            }
        }
    } satisfies MotionComponentProps<'a'> & MotionComponentProps<'button'>;

    return isAnchorButton(props)
        ? <motion.a {...buttonProps} href={props.href} ref={props.ref}>{buttonContent}</motion.a>
        : <motion.button {...buttonProps} ref={props.ref}>{buttonContent}</motion.button>;
};

const IconButton: FC<Props> = (props) => {
    const buttonProps = {
        'className': clsx(styles.buttonIcon, styles[`size-${props.size}`]),
        'type': props.type ?? 'button',
        'disabled': props.disabled || props.loading,
        'aria-disabled': props.disabled || props.loading,
        'onClick': (e) => {
            if (isAnchorButton(props)) {
                if (props.disabled) {
                    e.preventDefault();
                }
                props.onClick?.(e as MouseEvent<HTMLAnchorElement>);
            }
            else {
                props.onClick?.(e as MouseEvent<HTMLButtonElement>);
            }
        }
    } satisfies MotionComponentProps<'a'> & MotionComponentProps<'button'>;

    const buttonContent = (
        <ButtonIconWrapper size={ICON_SIZE[props.size ?? 's']}>
            {props.icon}
        </ButtonIconWrapper>
    );
    return isAnchorButton(props)
        ? <motion.a {...buttonProps} href={props.href} ref={props.ref}>{buttonContent}</motion.a>
        : <motion.button {...buttonProps} ref={props.ref}>{buttonContent}</motion.button>;
};

export const Button: FC<Props> = (props) => {
    const properties: Props = {
        ...DEFAULT_PROPS,
        ...props
    };

    return props.icon && !props.children ? <IconButton {...properties} /> : <StandartButton {...properties} />;
};

Button.displayName = 'Button';
